import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { type AnalyticValues, useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRestoreUserSettings } from "../../hooks/useUserSettings";
import { PRINTOUT_POPUP_RESTORE_FIELDS } from "./printoutSettingsPayload";

type Params = {
	/**
	 * Waits for any saved-printout edit still on its way, so it can't land after the replacement.
	 * Resolves whether those saves worked — a failed one has already shown its own snackbar.
	 */
	flushPresetSave: () => Promise<boolean>;
	/**
	 * Sends Printout 1's waiting autosave now, if there is one, and resolves whether it worked — so the
	 * user's own edits are saved before anything replaces or copies them. A failure keeps them on screen.
	 */
	flushPrintout1Save: () => Promise<boolean>;
	/** Printout 1's existing Save as default, with the saved printouts included. Rejects on failure. */
	savePrintout1AsDefault: () => Promise<void>;
	/** The restore's answer is in the user-settings cache: the popup can show and preview the department's Printout 1. */
	onDepartmentSettingsRestored: () => void;
	/** Back on Printout 1 with the list reloaded, this session's saved-printout edits forgotten. */
	resetToDepartmentPrintouts: () => Promise<void>;
};

type Running = "saveAsDefault" | "restore" | null;

/** Analytics `reason` when an edit still waiting to be saved failed to save, so the action stopped. */
const PENDING_SAVE_FAILED = "pending_save_failed";

const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/**
 * The two ways the department's printouts meet a user's in the Download Day view popup:
 * - **Save as default** (support only): Printout 1 and the saved printouts become the department's —
 *   replacing everyone's saved printouts and putting everyone on Printout 1.
 * - **Use department printouts** (any head nurse): the user's Printout 1 settings and saved printouts are
 *   replaced with the department's, and they're put on Printout 1.
 *
 * Both first save any edit still waiting — saved printouts, then Printout 1 — so nothing saves over the
 * replacement afterwards, and stop if one of those saves failed: Save as default would otherwise copy an
 * older version than the one on screen. A saved printout's failure has its own snackbar; Printout 1's
 * save has none, so this hook shows one for it. Any other failure is shown (snackbar) and tracked here.
 * Each resolves whether it succeeded.
 */
export const useDepartmentPrintouts = ({
	flushPresetSave,
	flushPrintout1Save,
	savePrintout1AsDefault,
	onDepartmentSettingsRestored,
	resetToDepartmentPrintouts,
}: Params) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const { mutateAsync: restoreFromDepartment } = useRestoreUserSettings({ includePrintoutPresets: true });
	/** Covers the whole run — the flushes before the request too — so a second click can't start another. */
	const [running, setRunning] = useState<Running>(null);

	/** Saves both kinds of waiting edit; resolves whether the action may go on (a stop is tracked under `errorEvent`). */
	const savePendingEdits = useCallback(
		async (errorEvent: string, props: AnalyticValues): Promise<boolean> => {
			if (!(await flushPresetSave())) {
				track(errorEvent, { ...props, reason: PENDING_SAVE_FAILED });
				return false;
			}
			if (!(await flushPrintout1Save())) {
				track(errorEvent, { ...props, reason: PENDING_SAVE_FAILED });
				showErrorSnackbar(t("printout.printout1.save.failed"), props);
				return false;
			}
			return true;
		},
		[flushPresetSave, flushPrintout1Save, track, showErrorSnackbar, t],
	);

	const saveAsDefault = useCallback(async (): Promise<boolean> => {
		const props = { department_id: departmentId, include_printout_presets: true };
		track("printout_save_as_default_start", props);
		setRunning("saveAsDefault");
		try {
			if (!(await savePendingEdits("printout_save_as_default_error", props))) return false;
			await savePrintout1AsDefault();
			await resetToDepartmentPrintouts();
		} catch (error) {
			track("printout_save_as_default_error", props);
			trackError("printout_save_as_default", { ...props, error_message: errorMessage(error) });
			showErrorSnackbar(t("printout.save.as.default.failed"), props, error);
			return false;
		} finally {
			setRunning(null);
		}
		track("printout_save_as_default_success", props);
		return true;
	}, [departmentId, savePendingEdits, savePrintout1AsDefault, resetToDepartmentPrintouts, track, trackError, showErrorSnackbar, t]);

	const restoreDepartmentPrintouts = useCallback(async (): Promise<boolean> => {
		const props = { department_id: departmentId };
		track("printout_restore_department_start", props);
		setRunning("restore");
		try {
			if (!(await savePendingEdits("printout_restore_department_error", props))) return false;
			// Updates the user-settings cache from the answer, so Printout 1 shows the department's settings.
			await restoreFromDepartment([...PRINTOUT_POPUP_RESTORE_FIELDS]);
			onDepartmentSettingsRestored();
			await resetToDepartmentPrintouts();
		} catch (error) {
			track("printout_restore_department_error", props);
			trackError("printout_restore_department", { ...props, error_message: errorMessage(error) });
			showErrorSnackbar(t("printout.restore.department.failed"), props, error);
			return false;
		} finally {
			setRunning(null);
		}
		track("printout_restore_department_success", props);
		return true;
	}, [
		departmentId,
		savePendingEdits,
		restoreFromDepartment,
		onDepartmentSettingsRestored,
		resetToDepartmentPrintouts,
		track,
		trackError,
		showErrorSnackbar,
		t,
	]);

	return {
		saveAsDefault,
		restoreDepartmentPrintouts,
		isSavingAsDefault: running === "saveAsDefault",
		isRestoring: running === "restore",
	};
};
